import { useState, useEffect, FormEvent } from 'react';

import { trackEvent } from 'lib/analytics';

/**
 * Form hook for handling processing and error messages.
 * Usage:
 *   `const {processing,errorMessage,onSubmit} = useForm(submitDataPromise().then(...))`
 */
export const useForm = (
  formName: string,
  submitPromise: () => Promise<{ redirect?: string } | void>,
  { startAsProcessing } = { startAsProcessing: false }
) => {
  useEffect(() => trackEvent(`form_${formName}_open`), [formName]);
  const [processing, setProcessing] = useState(startAsProcessing);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [redirect, setRedirect] = useState<string | null>(null);

  const onSubmit = (event?: Event | FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    trackEvent(`form_${formName}_saving`);

    setErrorMessage('');
    setProcessing(true);
    submitPromise()
      .then(response => {
        trackEvent(`form_${formName}_saved`);
        if (response && response.redirect) {
          // If we wanna to redirect to another page after success
          setRedirect(response.redirect);
        }
      })
      .catch(error => {
        console.warn(error);
        trackEvent(`form_${formName}_fail`);
        if (error?.message) {
          setErrorMessage(error.message);
        }
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  return {
    processing,
    errorMessage,
    onSubmit,
    redirect,
  };
};
